import type { ComponentProps, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

import { Button as ShadButton } from './button'
import { Card as ShadCard } from './card'
import { Badge as ShadBadge } from './badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { badgeColor } from '@/lib/utils'

/**
 * The app's own component vocabulary, built on shadcn/ui.
 *
 * Two reasons this layer exists rather than the pages using shadcn directly:
 *
 * 1. **Touch targets.** shadcn's default button is `h-8` — 32px. This app is
 *    used one-handed on a phone, often with work gloves, next to a running
 *    machine. Everything interactive here is at least 44px *on a touch
 *    screen*. That is a hard requirement of the product, not a style
 *    preference, so it is enforced in one place instead of being remembered at
 *    every call site.
 *
 *    The height is the `control` spacing token (index.css), not a literal
 *    `min-h-11`: with a mouse it drops to 36px, because a pointer one pixel
 *    wide gains nothing from a 44px target and the desk screens were spending
 *    a third of their height on it. On touch the token *is* 44px.
 *
 * 2. **The migration.** Thirteen screens already import `Button`, `Card`,
 *    `Field` and friends from `@/components/ui`. Keeping that surface meant the
 *    move to shadcn changed how things look without rewriting every page.
 *
 * New code can import shadcn components directly from `@/components/ui` too —
 * the barrel re-exports Table, Tabs, Sidebar, Sheet and the rest.
 */

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

/** The app's vocabulary mapped onto shadcn's. */
const VARIANT_MAP = {
  primary: 'default',
  secondary: 'outline',
  ghost: 'ghost',
  danger: 'destructive',
} as const

interface ButtonProps extends Omit<ComponentProps<typeof ShadButton>, 'variant' | 'size'> {
  variant?: ButtonVariant
  loading?: boolean
  fullWidth?: boolean
}

export function Button({
  variant = 'primary',
  loading,
  fullWidth,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <ShadButton
      variant={VARIANT_MAP[variant]}
      disabled={disabled || loading}
      className={cn(
        // Overrides shadcn's h-8. See the note at the top of this file.
        'min-h-control gap-2 px-control-x text-sm',
        /*
         * iOS proportions: a generous continuous radius and a semibold label.
         * The press feedback matters more than it looks — a physical control
         * acknowledges a touch immediately, and on plant wifi the *response*
         * can be a second away, so without this the worker's first assumption
         * is that the tap missed and they press again.
         */
        'rounded-xl font-semibold transition-[transform,background-color,box-shadow]',
        'active:scale-[0.97] disabled:active:scale-100',
        /*
         * `shrink` alongside `w-full`, and it is load-bearing.
         *
         * shadcn's base class is `shrink-0`. A full-width button in a flex row
         * therefore demanded 100% of the row and refused to give any of it
         * back, so whatever sat beside it was pushed straight out of the
         * container — on `/prijave/nova` that put "Odustani" 68px off the right
         * edge of a 1900px screen and gave the whole page a horizontal
         * scrollbar. The same shape was waiting on the 5S page, where both
         * buttons are full width and the row turns horizontal at `sm`: two
         * unshrinkable 100% buttons in one row is 200%.
         *
         * Letting them shrink is what "full width" should have meant all along:
         * take the whole row when you are alone in it, share it when you are
         * not.
         */
        fullWidth && 'w-full shrink',
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </ShadButton>
  )
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

/**
 * A raised surface.
 *
 * The shadow is deliberate rather than decorative: `<main>` sits on a sunken
 * canvas, so a card that was flat and outlined read as a box drawn on the same
 * sheet of paper. A soft shadow is what separates the two layers.
 *
 * **`children` are direct children, and that matters.** This used to nest them
 * inside a `CardContent`, which meant a `className` passed by a caller landed on
 * the outer element while the content sat one level down. So
 * `<Card className="grid sm:grid-cols-3">` made the *wrapper* a three-column
 * grid containing exactly one item, and the three fields inside stacked in a
 * narrow column — visible on the 5S filter bar, and silently true of every
 * `flex`/`grid`/`space-y` class any caller had ever passed to a Card. The
 * padding moved onto this element so the class and the content share it.
 */
export function Card({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <ShadCard
      className={cn(
        // rounded-2xl and a hairline rather than a full-strength border: this
        // is the iOS "inset grouped" surface, where the card is defined by its
        // fill and radius and the border only stops it dissolving into the
        // canvas behind it.
        //
        // `p-card` is the density token: 16–20px on touch, 16px with a mouse.
        'gap-0 rounded-2xl border-black/5 p-card shadow-sm dark:border-white/10',
        /*
         * Glass at 65%.
         *
         * Cards carry the body text, the tables and the numbers, so this is the
         * surface where transparency costs the most. Measured by compositing a
         * card over the deepest part of the ambient gradient — the worst case on
         * the page — against the 4.5 WCAG AA asks for normal text:
         *
         *              light   dark
         *   body text   16.62  14.97
         *   muted text   5.21   6.13
         *
         * Muted text in light mode is the binding constraint at 5.21. Note that
         * alpha buys less here than it looks: the card fill and the page behind
         * it are both near-white in light mode, so most of the visible glass
         * comes from the gradient showing through rather than from the number
         * itself — going from 75% to 65% moved muted text by 0.32.
         *
         * If it ever needs to go glassier, darken `--text-muted` first rather
         * than dropping this further. The text that suffers is the timestamps
         * and reference numbers, read at arm's length in a wood plant.
         */
        'supports-[backdrop-filter]:bg-card/65 backdrop-blur-xl backdrop-saturate-150',
        // Barely visible in light mode, but in dark mode a shadow does nothing
        // at all — the border is what carries the edge there.
        'dark:shadow-none',
        className,
      )}
      {...props}
    >
      {children}
    </ShadCard>
  )
}

export function CardTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3 pointer-fine:mb-3">
      <h2 className="text-base font-semibold">{children}</h2>
      {action}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

/**
 * Colour comes from the API, not from a variant.
 *
 * The server decides that RESOLVED is emerald and URGENT is rose, so the
 * meaning lives in one place and the client only maps a name to classes.
 * shadcn's own variants (default/secondary/destructive) cannot express that.
 */
export function Badge({
  color,
  children,
  className,
}: {
  color?: string | null
  children: ReactNode
  className?: string
}) {
  return (
    <ShadBadge
      variant="secondary"
      className={cn(
        'border-transparent px-2.5 py-1 font-medium',
        // A badge is read, not pressed, so the desk version can be a row's
        // height smaller without costing anybody a target.
        'pointer-fine:px-2 pointer-fine:py-0.5',
        badgeColor(color),
        className,
      )}
    >
      {children}
    </ShadBadge>
  )
}

// Form controls (Field, Input, Textarea, Select, PasswordInput, NumberInput,
// DateInput) live in ./form.tsx and are re-exported by the barrel.

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

/**
 * A dialog whose title and buttons stay put while the body scrolls.
 *
 * The previous version put `max-h-[85dvh] overflow-y-auto` on the popup itself,
 * so the whole thing scrolled as one. On the user form — twelve fields on a
 * phone — that meant the title scrolled away and "Sačuvaj" sat below the fold,
 * reachable only after scrolling to the very bottom. Three rows fix it: header,
 * scrollable body, footer.
 *
 * `minmax(0, 1fr)` on the middle row is doing real work. A plain `1fr` track
 * has an automatic minimum of min-content, so it refuses to shrink below its
 * contents and the popup overflows the viewport instead of the body scrolling —
 * the exact bug this was meant to fix. Do not simplify it.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  busy = false,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  /** Announced after the title. Also silences Base UI's missing-description warning. */
  description?: string
  children: ReactNode
  footer?: ReactNode
  /**
   * Blocks Escape and backdrop clicks while a save is in flight.
   *
   * On plant wifi a request can take several seconds. Dismissing the dialog
   * mid-flight leaves the user with no idea whether their edit landed.
   */
  busy?: boolean
  /**
   * Desktop width. `lg` is for dialogs holding a grid rather than a form — the
   * badge sheet, where a one-column dialog would mean scrolling past twenty
   * cards to reach the print button.
   *
   * Only the desktop width changes: below `sm` every dialog is a full-width
   * sheet, and that is the right answer for all of them.
   */
  size?: 'md' | 'lg'
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next && !busy) onClose()
      }}
    >
      <DialogContent
        className={cn(
          'grid max-h-[85dvh] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 p-0',
          size === 'lg' ? 'sm:max-w-3xl' : 'sm:max-w-lg',
          /*
           * A sheet on a phone, a centred dialog on a desktop.
           *
           * Pinned to the bottom edge below `sm` with only its top corners
           * rounded, because that is both the platform idiom and the practical
           * one: a dialog centred vertically puts its fields under the keyboard
           * the moment one is focused, while a sheet grows upward from the
           * bottom and keeps them above it.
           */
          'top-auto bottom-0 max-h-[92dvh] translate-y-0 rounded-b-none rounded-t-3xl',
          'sm:top-1/2 sm:bottom-auto sm:max-h-[85dvh] sm:-translate-y-1/2 sm:rounded-2xl',
          'border-black/5 dark:border-white/10',
          // The dialog floats over the page rather than replacing it.
          'supports-[backdrop-filter]:bg-popover/80 backdrop-blur-2xl backdrop-saturate-150',
        )}
        showCloseButton={!busy}
      >
        {/* The grabber. Purely a signal that this thing came from the bottom
            edge and goes back there; hidden once it is a centred dialog. */}
        <div className="flex justify-center pt-2 sm:hidden" aria-hidden>
          <span className="bg-muted-foreground/30 h-1 w-9 rounded-full" />
        </div>

        <DialogHeader className="px-4 pt-3 pb-3 sm:pt-4 pointer-fine:pt-3 pointer-fine:pb-2">
          <DialogTitle className="pr-8">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* min-h-0 so this row may actually shrink; see the note above. */}
        <div className="min-h-0 overflow-y-auto px-4 pb-4">{children}</div>

        {/* DialogFooter's default -mx-4 -mb-4 assumes the popup has p-4 padding.
            This one does not, so those pull-outs are reset. */}
        {footer && (
          <DialogFooter className="mx-0 mb-0 pointer-fine:px-4 pointer-fine:py-3">{footer}</DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function Spinner({ label = 'Učitavanje…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10" role="status">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span className="muted text-sm">{label}</span>
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {icon && <div className="muted mb-3">{icon}</div>}
      <p className="font-medium">{title}</p>
      {description && <p className="muted mt-1 max-w-sm text-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <EmptyState
      title="Nešto nije u redu"
      description={message}
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            Pokušaj ponovo
          </Button>
        ) : undefined
      }
    />
  )
}

/**
 * A single dashboard number.
 *
 * The delta carries an arrow and a word as well as colour, so the direction is
 * readable to someone who cannot distinguish red from green — roughly one man
 * in twelve.
 */
export function StatTile({
  label,
  value,
  delta,
  hint,
  tone = 'neutral',
}: {
  label: string
  value: string
  delta?: number | null
  hint?: string
  tone?: 'neutral' | 'good' | 'warn' | 'bad'
}) {
  const toneClasses = {
    neutral: '',
    good: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    bad: 'text-rose-600 dark:text-rose-400',
  }[tone]

  return (
    <Card className="flex flex-col justify-between">
      <p className="muted text-xs font-medium tracking-wide uppercase">{label}</p>
      {/* The display face earns its keep on the big numbers, which is the one
          place on a dashboard anybody actually looks at the type. */}
      <p
        className={cn(
          'font-heading mt-2 text-2xl font-semibold tabular-nums sm:text-3xl',
          toneClasses,
        )}
      >
        {value}
      </p>
      {delta !== null && delta !== undefined && (
        <p className="mt-1 text-xs font-medium">
          <span className={delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
          </span>
          <span className="muted"> u odnosu na prethodni period</span>
        </p>
      )}
      {hint && <p className="muted mt-1 text-xs">{hint}</p>}
    </Card>
  )
}
